import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import HeroImage from "./HeroImage";

// ✨ Typewriter effect
const TypewriterText = ({ text, speed = 32 }) => {
  const [displayedText, setDisplayedText] = React.useState("");

  React.useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, index + 1));
      index++;
      if (index === text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <span className="inline-block text-gray-600">
      {displayedText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

const Hero = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Assalamu’alaikum, saya ingin informasi layanan Aqiqah, Catering, dan Qurban di Toyibah Aqiqah."
    );
    window.open(`https://wa.me/6287884243038?text=${message}`, "_blank");
  };

  const scrollToPackages = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero" // ✅ Supaya link #hero di header berfungsi
      className="relative min-h-[90vh] flex items-center justify-center bg-gradient-to-br from-[#FFF5F2] via-[#FFEAE3] to-[#FFF5F2] overflow-hidden px-6 py-20 md:py-28 pt-24"
    >
      {/* Soft background pattern */}
      <div
        className="absolute inset-0 z-0 opacity-[0.07]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23FF8A65' fill-opacity='0.7'%3E%3Ccircle cx='40' cy='40' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-[#FFE0D3]/40 via-transparent to-[#FFF5F2] z-0" />

      {/* Ambient glow */}
      <div className="absolute -top-10 -left-10 w-72 h-72 bg-[#FFC4B2]/50 blur-[100px] rounded-full animate-pulse" />
      <div className="absolute bottom-10 right-0 w-64 h-64 bg-[#58B09C]/40 blur-[90px] rounded-full animate-pulse delay-1000" />

      <div className="relative z-10 w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 items-center gap-10 lg:gap-20">
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center lg:text-left flex flex-col items-center lg:items-start px-4"
        >
          {/* Greeting badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/80 backdrop-blur-md border border-[#FFC4B2] shadow-md text-[#FF8A65] font-semibold text-sm"
          >
            <HeroImage />
            <span>Assalamu’alaikum Ayah & Bunda</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-[#2E2E2E]"
          >
            Solusi Aqiqah,{" "}
            <span className="whitespace-nowrap">
              Catering <span className="text-[#FF8A65]">&amp; Qurban</span>
            </span>
            <span className="block text-[#58B09C]">
              Terpercaya di Jabodetabek
            </span>
          </motion.h1>

          {/* SEO Description */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-gray-600 text-lg md:text-xl leading-relaxed max-w-xl mt-4 mb-10"
          >
            <TypewriterText
              text="Toyibah Aqiqah menyediakan layanan aqiqah syar’i, catering halal, dan qurban berkualitas di wilayah Jabodetabek. Praktis, amanah, lezat, dan penuh keberkahan untuk keluarga tercinta."
            />
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button
              onClick={handleWhatsAppClick}
              className="bg-gradient-to-r from-[#FF8A65] to-[#FF6B4A] text-white font-bold text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Konsultasi Sekarang
            </Button>

            <Button
              onClick={scrollToPackages}
              variant="outline"
              className="border-2 border-[#FF8A65] text-[#FF8A65] hover:bg-[#FF8A65] hover:text-white font-bold text-lg px-8 py-6 rounded-xl transition-all hover:scale-105"
            >
              Lihat Layanan
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </motion.div>

        {/* Right Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="flex justify-center items-center relative"
        >
          <div className="absolute bg-[#FF8A65]/40 blur-[120px] rounded-full w-[300px] h-[300px]" />
          <img
            src="/TOYIBAH_AQIQAH.png"
            alt="Toyibah Aqiqah - Aqiqah, Catering & Qurban"
            className="relative w-[260px] md:w-[340px] lg:w-[400px] object-contain drop-shadow-[0_0_35px_rgba(255,138,101,0.4)]"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
